import moment from "moment";
import {dateFormat} from "@/utils/constants";

let jsPDF;
try {
	jsPDF = require("jspdf").default || require("jspdf");
} catch (error) {
	console.error("jsPDF non è installato. Esegui: npm install jspdf");
	jsPDF = null;
}

const getCardNumber = (player) =>
	player?.cardNumber ||
	player?.registrationNumber ||
	player?.membershipNumber ||
	player?.tesseraNumber ||
	player?.federationNumber ||
	player?.federalNumber ||
	player?.federationId ||
	player?.federalId ||
	player?.licenseNumber ||
	"";

export const generateMatchLineupPDF = (matchData, teamData) => {
	if (!jsPDF) {
		throw new Error(
			"jsPDF non è installato. Per utilizzare questa funzionalità, installa jsPDF eseguendo: npm install jspdf",
		);
	}

	// =========================
	// Document setup (A4, pt)
	// =========================
	const doc = new jsPDF({unit: "pt", format: "a4"});
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();

	// =========================
	// Theme + spacing (premium)
	// =========================
	const theme = {
		primary: [16, 89, 191],
		primaryDark: [9, 53, 128],
		primaryLight: [59, 130, 246],
		bg: [245, 247, 250],
		border: [229, 231, 235],
		text: [30, 41, 59],
		muted: [100, 116, 139],
		success: [34, 197, 94],
	};

	const SP = {
		pagePad: 42,
		blockGap: 12,
		cardPad: 12,
		footerH: 54,
	};

	// =========================
	// Helpers
	// =========================
	const safeText = (value, fallback = "---") =>
		value === null || value === undefined || value === ""
			? fallback
			: String(value);

	const setOpacity = (opacity) => {
		try {
			if (doc.GState) doc.setGState(new doc.GState({opacity}));
		} catch (_) {
			/* no-op */
		}
	};

	const resetOpacity = () => {
		try {
			if (doc.GState) doc.setGState(new doc.GState({opacity: 1}));
		} catch (_) {
			/* no-op */
		}
	};

	const ellipsize = (
		text,
		maxWidth,
		fontSize = 11,
		font = ["helvetica", "normal"],
	) => {
		const s = safeText(text, "");
		if (!s) return "";
		doc.setFont(font[0], font[1]);
		doc.setFontSize(fontSize);
		if (doc.getTextWidth(s) <= maxWidth) return s;

		const ell = "…";
		let lo = 0;
		let hi = s.length;
		while (lo < hi) {
			const mid = Math.floor((lo + hi) / 2);
			const candidate = s.slice(0, mid) + ell;
			if (doc.getTextWidth(candidate) <= maxWidth) lo = mid + 1;
			else hi = mid;
		}
		return s.slice(0, Math.max(0, lo - 1)) + ell;
	};

	const drawCard = (x, y, w, h, {radius = 12, shadow = true} = {}) => {
		if (shadow) {
			doc.setFillColor(0, 0, 0);
			setOpacity(0.06);
			doc.roundedRect(x + 2, y + 3, w, h, radius, radius, "F");
			resetOpacity();
		}
		doc.setDrawColor(...theme.border);
		doc.setLineWidth(0.8);
		doc.setFillColor(255, 255, 255);
		doc.roundedRect(x, y, w, h, radius, radius, "FD");
	};

	// =========================
	// Match meta
	// =========================
	const matchTitle =
		`${safeText(matchData?.homeClub, "")} - ${safeText(matchData?.awayClub, "")}`.trim();
	const matchDate = matchData?.startDate
		? moment(matchData.startDate).format(`${dateFormat} HH:mm`)
		: "---";

	const title = `Distinta - ${safeText(teamData?.club, "")}`.trim();

	// =========================
	// Layout state
	// =========================
	let y = 0;

	// Table columns (tuned widths)
	const columns = [
		{label: "N°", width: 46, align: "center"},
		{label: "Giocatore", width: 252, align: "left"},
		{label: "Nascita", width: 110, align: "center"},
		{label: "N° tessera", width: 120, align: "center"},
	];

	const rowPad = 10;
	const lineHeight = 14;

	const tableWidth = columns.reduce((sum, c) => sum + c.width, 0);
	const startX = SP.pagePad;

	// Center the table if there is space (clean look)
	const tableX = Math.max(startX, (pageWidth - tableWidth) / 2);

	// =========================
	// Header + Footer
	// =========================
	const drawHeader = () => {
		const headerH = 92;

		// background
		doc.setFillColor(...theme.primaryDark);
		doc.rect(0, 0, pageWidth, headerH, "F");

		// bottom band
		doc.setFillColor(...theme.primary);
		doc.rect(0, headerH - 20, pageWidth, 20, "F");

		// micro accent
		doc.setFillColor(...theme.primaryLight);
		doc.rect(0, headerH - 3, pageWidth, 3, "F");

		// title
		doc.setFont("helvetica", "bold");
		doc.setFontSize(20);
		doc.setTextColor(255, 255, 255);
		doc.text(title || "Distinta", SP.pagePad, 46);

		// subtitle
		doc.setFont("helvetica", "normal");
		doc.setFontSize(10);
		doc.setTextColor(235, 245, 255);

		const subtitle = [matchTitle, matchDate].filter(Boolean).join(" • ");
		doc.text(subtitle, SP.pagePad, 66);

		// watermark soft
		setOpacity(0.06);
		doc.setFont("helvetica", "bold");
		doc.setFontSize(42);
		doc.setTextColor(255, 255, 255);
		doc.text("DISTINTA", pageWidth - SP.pagePad, 56, {align: "right"});
		resetOpacity();

		doc.setTextColor(...theme.text);

		y = headerH + 18;
	};

	const drawFooter = () => {
		const totalPages = doc.internal.pages.length - 1;
		const generatedAt = `Generato il ${moment().format("DD/MM/YYYY • HH:mm")}`;

		for (let i = 1; i <= totalPages; i++) {
			doc.setPage(i);

			doc.setDrawColor(...theme.border);
			doc.setLineWidth(1);
			doc.line(
				SP.pagePad,
				pageHeight - SP.footerH + 18,
				pageWidth - SP.pagePad,
				pageHeight - SP.footerH + 18,
			);

			doc.setFont("helvetica", "normal");
			doc.setFontSize(9);
			doc.setTextColor(...theme.muted);

			doc.text(generatedAt, SP.pagePad, pageHeight - 18);
			doc.text(
				`Pagina ${i} / ${totalPages}`,
				pageWidth - SP.pagePad,
				pageHeight - 18,
				{
					align: "right",
				},
			);
		}

		doc.setTextColor(...theme.text);
	};

	// =========================
	// Table
	// =========================
	const drawTableHeader = () => {
		// Column header pill/card
		const headerH = 28;

		// subtle shadow + rounded header
		drawCard(tableX, y, tableWidth, headerH, {radius: 10, shadow: true});

		// fill header background (override white)
		doc.setFillColor(...theme.bg);
		doc.roundedRect(tableX, y, tableWidth, headerH, 10, 10, "F");

		doc.setDrawColor(...theme.border);
		doc.setLineWidth(0.8);
		doc.roundedRect(tableX, y, tableWidth, headerH, 10, 10, "S");

		doc.setFont("helvetica", "bold");
		doc.setFontSize(10);
		doc.setTextColor(...theme.text);

		let x = tableX;
		columns.forEach((col, idx) => {
			const textX = col.align === "center" ? x + col.width / 2 : x + rowPad;
			doc.text(col.label, textX, y + 18, {align: col.align});

			// vertical separators (subtle)
			if (idx > 0) {
				doc.setDrawColor(...theme.border);
				doc.setLineWidth(0.8);
				doc.line(x, y + 6, x, y + headerH - 6);
			}

			x += col.width;
		});

		y += headerH + 8;
	};

	const ensureSpace = (needed) => {
		if (y + needed > pageHeight - SP.footerH) {
			doc.addPage();
			drawHeader();
			drawTableHeader();
			return true;
		}
		return false;
	};

	const drawRow = (row, index) => {
		const nameLines = doc.splitTextToSize(
			safeText(row.playerName, "---"),
			columns[1].width - 2 * rowPad,
		);

		const rowH = Math.max(26, nameLines.length * lineHeight + 10);

		ensureSpace(rowH + 2);

		// zebra background
		if (index % 2 === 1) {
			doc.setFillColor(...theme.bg);
			doc.roundedRect(tableX, y, tableWidth, rowH, 10, 10, "F");
		}

		// row border
		doc.setDrawColor(...theme.border);
		doc.setLineWidth(0.8);
		doc.roundedRect(tableX, y, tableWidth, rowH, 10, 10, "S");

		let x = tableX;

		doc.setFont("helvetica", "normal");
		doc.setFontSize(10);
		doc.setTextColor(...theme.text);

		// N°
		doc.text(safeText(row.number, "---"), x + columns[0].width / 2, y + 18, {
			align: "center",
		});
		x += columns[0].width;

		// Giocatore
		doc.text(nameLines, x + rowPad, y + 18);
		x += columns[1].width;

		// Nascita
		doc.text(safeText(row.birthDate, "---"), x + columns[2].width / 2, y + 18, {
			align: "center",
		});
		x += columns[2].width;

		// Tessera
		doc.text(
			safeText(row.cardNumber, "---"),
			x + columns[3].width / 2,
			y + 18,
			{align: "center"},
		);

		y += rowH + 6;
	};

	// =========================
	// Build data + render
	// =========================
	drawHeader();

	// Small "meta" card (optional but nice): squadra / competizione / turno / campo etc if present
	{
		const x = SP.pagePad;
		const w = pageWidth - SP.pagePad * 2;
		const h = 56;

		// keep it simple; only if something to show
		const extras = [];
		if (matchData?.fieldName) extras.push({k: "Campo", v: matchData.fieldName});
		if (matchData?.round) extras.push({k: "Turno", v: matchData.round});
		if (matchData?.matchType) extras.push({k: "Tipo", v: matchData.matchType});
		if (matchData?.playersCategory)
			extras.push({k: "Categoria", v: matchData.playersCategory});

		if (extras.length > 0) {
			drawCard(x, y, w, h, {radius: 12, shadow: true});

			const colW = (w - SP.cardPad * 2) / Math.min(3, extras.length);
			const show = extras.slice(0, 3);

			show.forEach((it, idx) => {
				const cx = x + SP.cardPad + idx * colW;

				doc.setFont("helvetica", "bold");
				doc.setFontSize(9);
				doc.setTextColor(...theme.muted);
				doc.text(it.k, cx, y + 22);

				doc.setFont("helvetica", "bold");
				doc.setFontSize(12);
				doc.setTextColor(...theme.text);
				doc.text(
					ellipsize(safeText(it.v), colW - 10, 12, ["helvetica", "bold"]),
					cx,
					y + 42,
				);

				// divider
				if (idx > 0) {
					doc.setDrawColor(...theme.border);
					doc.setLineWidth(1);
					doc.line(cx - 10, y + 14, cx - 10, y + h - 14);
				}
			});

			y += h + SP.blockGap;
		}
	}

	drawTableHeader();

	const players = (teamData?.players || []).slice().sort((a, b) => {
		if (a.startingPlayer !== b.startingPlayer) return a.startingPlayer ? -1 : 1;

		const aNumber = parseInt(a.playerNumber || 0, 10);
		const bNumber = parseInt(b.playerNumber || 0, 10);
		if (aNumber !== bNumber) return aNumber - bNumber;

		const aName =
			`${a.player?.lastName || ""} ${a.player?.firstName || ""}`.trim();
		const bName =
			`${b.player?.lastName || ""} ${b.player?.firstName || ""}`.trim();
		return aName.localeCompare(bName);
	});

	players.forEach((p, idx) => {
		const playerName =
			p.player?.firstName && p.player?.lastName
				? `${p.player.firstName} ${p.player.lastName}`
				: "---";

		const birthDateValue =
			p.player?.dateOfBirth || p.player?.birthDate || p.player?.dob || "";

		const birthDate = birthDateValue
			? moment(birthDateValue).format(dateFormat)
			: "---";

		const cardNumber = safeText(getCardNumber(p.player), "---");

		drawRow(
			{
				number: p.playerNumber,
				playerName,
				birthDate,
				cardNumber,
			},
			idx,
		);
	});

	// Footer on all pages
	drawFooter();

	// =========================
	// Save
	// =========================
	const sanitize = (s) =>
		safeText(s, "")
			.replace(/[\\/:*?"<>|]/g, "-")
			.replace(/\s+/g, "_");

	const fileName = [
		"distinta",
		sanitize(teamData?.club || "squadra"),
		matchData?.startDate ? moment(matchData.startDate).format("YYYYMMDD") : "",
	]
		.filter(Boolean)
		.join("_");

	doc.save(fileName + ".pdf");
};
